import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchStatusData, fetchPrevious24HoursData } from './thunks';
import { DetectionStatusState, StatusDataItem } from 'src/types/detectionTypes';

const initialState: DetectionStatusState = {
	statusData: [
		{
			title: 'Total scans finished',
			count: 0,
			type: 'scansFinished',
			route: '/scanlog',
			status: 'idle',
		},
		{
			title: 'Total active detections',
			count: 0,
			type: 'activeDetections',
			detectionsbeforeDateCount: 0,
			route: 0,
			status: 'idle',
		},
		{
			title: 'Scans in progress',
			count: 0,
			type: 'scansInProgress',
			route: null,
			status: 'idle',
		},
		{
			title: 'Total scans remediated',
			count: 0,
			type: 'scansRemediated',
			route: 2,
			status: 'idle',
		},
		{
			title: 'Active policies',
			count: 0,
			type: 'activePolicies',
			totalCount: 0,
			route: '/settings/policy',
			status: 'idle',
		},
	],
	loading: false,
	error: null,
	isPolling: false,
	lastFetchTime: null,
};

const detectionStatusSlice = createSlice({
	name: 'detectionStatus',
	initialState,
	reducers: {
		resetDetectionStatusState: () => initialState,
		setIsPolling: (state, action: PayloadAction<boolean>) => {
			state.isPolling = action.payload;
		},
		updateStatusDataItem: (
			state,
			action: PayloadAction<{
				type: StatusDataItem['type'];
				count: number;
				status: StatusDataItem['status'];
				additionalCount?: number;
			}>,
		) => {
			const { type, count, status, additionalCount } = action.payload;
			state.statusData = state.statusData.map((item) =>
				item.type === type
					? {
							...item,
							count,
							status,
							...(additionalCount !== undefined
								? { totalCount: additionalCount }
								: {}),
					  }
					: item,
			);
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch Status Data
			.addCase(fetchStatusData.pending, (state) => {
				state.loading = true;
				state.error = null;
				// Set all items to loading status
				state.statusData = state.statusData.map((item) => ({
					...item,
					status: 'loading',
				}));
			})
			.addCase(fetchStatusData.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.lastFetchTime = new Date().toISOString();

				const results = action.payload;

				// Update statusData based on results
				state.statusData = state.statusData.map((item) => {
					switch (item.type) {
						case 'activeDetections':
							return {
								...item,
								count: results.activeDetections.count,
								status: results.activeDetections.status,
							};
						case 'scansRemediated':
							return {
								...item,
								count: results.scansRemediated.count,
								status: results.scansRemediated.status,
							};
						case 'scansFinished':
							return {
								...item,
								count: results.scansFinished.count,
								status: results.scansFinished.status,
							};
						case 'activePolicies':
							return {
								...item,
								count: results.activePolicies.count,
								totalCount: results.activePolicies.totalCount,
								status: results.activePolicies.status,
							};
						case 'scansInProgress':
							return {
								...item,
								count: results.scansInProgress.count,
								status: results.scansInProgress.status,
							};
						default:
							return item;
					}
				});
			})
			.addCase(fetchStatusData.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
				// Set all items to failed status
				state.statusData = state.statusData.map((item) => ({
					...item,
					status: 'failed',
				}));
			})
			// Fetch Previous 24 Hours Data
			.addCase(fetchPrevious24HoursData.pending, (state) => {
				state.error = null;
			})
			.addCase(fetchPrevious24HoursData.fulfilled, (state, action) => {
				state.error = null;
				state.statusData = state.statusData.map((item) =>
					item.type === 'activeDetections'
						? {
								...item,
								detectionsbeforeDateCount:
									action.payload.detectionsbeforeDateCount,
						  }
						: item,
				);
			})
			.addCase(fetchPrevious24HoursData.rejected, (state, action) => {
				state.error = action.payload as string;
			});
	},
});

export const { resetDetectionStatusState, setIsPolling, updateStatusDataItem } =
	detectionStatusSlice.actions;
export default detectionStatusSlice.reducer;
